import React, { useState, useEffect, useCallback } from 'react';
import { useMsal } from '@azure/msal-react';
import {
  Button,
  Input,
  MessageBar,
  MessageBarBody,
  Text,
  Divider,
} from '@fluentui/react-components';
import { DeleteRegular } from '@fluentui/react-icons';
import styles from './DomainAdmin.module.scss';
import { IDomainAdminProps } from './IDomainAdminProps';
import { getAuthToken } from '../../auth/getAuthToken';
import strings from './strings';

interface ApprovedDomain {
  id: number;
  domain: string;
  addedAt: string;
  addedByUpn: string;
}

const DomainAdmin: React.FC<IDomainAdminProps> = () => {
  const { accounts } = useMsal();
  const [domains, setDomains] = useState<ApprovedDomain[]>([]);
  const [newDomain, setNewDomain] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [removingId, setRemovingId] = useState<number | null>(null);
  const [adding, setAdding] = useState(false);

  const loadDomains = useCallback(async () => {
    setError(null);
    const account = accounts[0];
    if (!account) return;
    const token = await getAuthToken(account);
    if (!token) { setError(strings.errorLoad); return; }
    try {
      const res = await fetch('/api/domains', {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!res.ok) { setError(strings.errorLoad); return; }
      const data: ApprovedDomain[] = await res.json();
      setDomains(data);
    } catch {
      setError(strings.errorLoad);
    }
  }, [accounts]);

  useEffect(() => { loadDomains(); }, [loadDomains]);

  const handleAdd = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    const domain = newDomain.trim().toLowerCase();
    if (!domain) return;
    setError(null);
    setAdding(true);
    const account = accounts[0];
    if (!account) return;
    const token = await getAuthToken(account);
    if (!token) { setError(strings.errorAdd); setAdding(false); return; }
    try {
      const res = await fetch('/api/domains', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ domain }),
      });
      if (res.status === 409) { setError(strings.errorDuplicate); return; }
      if (!res.ok) { setError(strings.errorAdd); return; }
      setNewDomain('');
      await loadDomains();
    } catch {
      setError(strings.errorAdd);
    } finally {
      setAdding(false);
    }
  }, [newDomain, loadDomains, accounts]);

  const handleRemove = useCallback(async (id: number) => {
    setError(null);
    setRemovingId(id);
    const account = accounts[0];
    if (!account) return;
    const token = await getAuthToken(account);
    if (!token) { setError(strings.errorRemove); setRemovingId(null); return; }
    try {
      const res = await fetch(`/api/domains/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!res.ok) { setError(strings.errorRemove); return; }
      setDomains(prev => prev.filter(d => d.id !== id));
    } catch {
      setError(strings.errorRemove);
    } finally {
      setRemovingId(null);
    }
  }, [accounts]);

  return (
    <section className={styles.container} aria-label={strings.heading}>
      <Text as="h2" weight="semibold" size={500} className={styles.heading}>
        {strings.heading}
      </Text>

      <form className={styles.addForm} onSubmit={handleAdd}>
        <Input
          value={newDomain}
          onChange={(_, d) => setNewDomain(d.value)}
          placeholder={strings.addPlaceholder}
          aria-label={strings.addLabel}
          autoComplete="off"
          appearance="outline"
          className={styles.addInput}
        />
        <Button
          appearance="primary"
          type="submit"
          disabled={adding || !newDomain.trim()}
        >
          {strings.addButton}
        </Button>
      </form>

      {error && (
        <MessageBar intent="error" aria-live="assertive">
          <MessageBarBody>{error}</MessageBarBody>
        </MessageBar>
      )}

      <Divider />

      {domains.length === 0 ? (
        <Text className={styles.emptyState}>{strings.emptyState}</Text>
      ) : (
        <ul className={styles.list}>
          {domains.map(d => (
            <li key={d.id} className={styles.listItem}>
              <Text className={styles.domain}>{d.domain}</Text>
              <Button
                appearance="subtle"
                icon={<DeleteRegular />}
                size="small"
                disabled={removingId === d.id}
                onClick={() => handleRemove(d.id)}
                aria-label={`${strings.removeButton} ${d.domain}`}
                className={styles.removeButton}
              >
                {strings.removeButton}
              </Button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};

export default DomainAdmin;
